import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import CommonHeadingNBtns from "../../common-components/CommonHeadingNBtns";
import {
  add_centralised_plan_category,
  update_centralised_plan_category,
} from "../../../data/ApiEndpoints";
import { commonPostPutApi } from "../../../redux/reducers/commonApiSlice";
import { showConfirmAlert } from "../../components-react/ReactAlert";
import ReactQuillTextEditor from "../../components-react/inputs/ReactQuillTextEditor";

const PlanCategoryForm = ({
  userInfoData,
  rowData,
  isEdit,
  closeModal,
  callFunction,
}) => {
  const dispatch = useDispatch();
  const { centralisedPlanCategoriesLoading } = useSelector(
    (state) => state.plans
  );

  const initialFormValues = {
    categoryName: isEdit && rowData?.name ? rowData.name : "",
    categoryDescription:
      isEdit && rowData?.description ? rowData.description : "",
    minPrice: isEdit && rowData?.min_price ? rowData.min_price : "",
    gymTransferFee:
      isEdit && rowData?.gym_transfer_fee ? rowData.gym_transfer_fee : "",
    membershipTransferFee:
      isEdit && rowData?.subscription_transfer_fee
        ? rowData.subscription_transfer_fee
        : "",
    isActive: isEdit && rowData?.status === "active" ? true : false,
  };
  const [formValues, setFormValues] = useState(initialFormValues);
  const [formErrors, setFormErrors] = useState({});

  const onChangeInput = (e) => {
    const { name, value, type, checked } = e.target;

    setFormValues((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const onChangeTextInput = async (content, fieldName) => {
    setFormValues((prev) => ({ ...prev, [fieldName]: content }));
  };

  const validateForm = (values) => {
    const errors = {};

    if (!values.categoryName.trim()) {
      errors.categoryName = "Category name is required!";
    }

    if (!values.categoryDescription.trim()) {
      errors.categoryDescription = "Category description is required!";
    }

    if (values.minPrice && isNaN(values.minPrice)) {
      errors.minPrice = "Min price must be a valid number!";
    }

    if (!values.gymTransferFee) {
      errors.gymTransferFee = "Gym transfer fee is required!";
    } else if (isNaN(values.gymTransferFee)) {
      errors.gymTransferFee = "Gym transfer fee must be a valid number!";
    }

    if (!values.membershipTransferFee) {
      errors.membershipTransferFee = "Membership transfer fee is required!";
    } else if (isNaN(values.membershipTransferFee)) {
      errors.membershipTransferFee =
        "Membership transfer fee must be a valid number!";
    }

    return errors;
  };

  const handleSubmitForm = async (payload) => {
    const method = isEdit ? "PUT" : "POST";
    const endpoint = isEdit
      ? update_centralised_plan_category
      : add_centralised_plan_category;
    const token = userInfoData?.token || "";

    try {
      const result = await dispatch(
        commonPostPutApi({ method, endpoint, payload, token })
      );

      if (result?.payload?.status) {
        setFormValues(initialFormValues);
        callFunction();
        closeModal();
        toast.success(result?.payload?.message || "Success!");
      } else {
        const errorMessage = result?.payload?.message || result?.error?.message;
        toast.error(errorMessage || "Something Went Wrong!");
      }
    } catch (error) {
      console.error("Error making POST request:", error);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const errors = validateForm(formValues);
    setFormErrors(errors);

    if (Object.keys(errors).length === 0) {
      const userData = {
        name: formValues.categoryName || "",
        description: formValues.categoryDescription || "",
        status: formValues.isActive ? "active" : "inactive",
        min_price: formValues.minPrice || "",
        gym_transfer_fee: formValues.gymTransferFee || "",
        subscription_transfer_fee: formValues.membershipTransferFee || "",
      };

      if (isEdit) {
        userData.uid = rowData?.uid || "";
      }

      const handleConfirm = async () => {
        handleSubmitForm(userData);
      };

      showConfirmAlert(
        handleConfirm,
        `Are you sure!`,
        "Edit Form",
        "Submit Form"
      );
    }
  };

  return (
    <>
      <CommonHeadingNBtns
        title={`${isEdit ? "Update" : "Add"} Gym Category`}
        onClose={closeModal}
        btnTxt={isEdit ? "Update" : "Add"}
        onClick={onSubmit}
        isLoading={centralisedPlanCategoriesLoading}
      />

      <form className="formContainer" onSubmit={onSubmit}>
        <div className="formInputsGrid twoGridSystem">
          <div className="formInputBox">
            <label htmlFor="categoryName" className="formInputLabel">
              Category Name <span className="required">*</span>
            </label>
            <input
              type="text"
              placeholder="Enter Category Name"
              className={`formInput ${
                formErrors.categoryName ? "formError" : ""
              }`}
              name="categoryName"
              value={formValues.categoryName}
              onChange={onChangeInput}
            />
            <span className="error-label">{formErrors.categoryName || ""}</span>
          </div>

          <div className="formInputBox">
            <label htmlFor="minPrice" className="formInputLabel">
              Min Price
            </label>
            <input
              type="number"
              placeholder="Enter Min Price"
              className={`formInput ${formErrors.minPrice ? "formError" : ""}`}
              name="minPrice"
              value={formValues.minPrice}
              onChange={onChangeInput}
            />
            <span className="error-label">{formErrors.minPrice || ""}</span>
          </div>

          <div className="formInputBox">
            <label htmlFor="gymTransferFee" className="formInputLabel">
              Gym Transfer Fee <span className="required">*</span>
            </label>
            <input
              type="number"
              placeholder="Enter Gym Transfer Fee"
              className={`formInput ${
                formErrors.gymTransferFee ? "formError" : ""
              }`}
              name="gymTransferFee"
              value={formValues.gymTransferFee}
              onChange={onChangeInput}
            />
            <span className="error-label">
              {formErrors.gymTransferFee || ""}
            </span>
          </div>

          <div className="formInputBox">
            <label htmlFor="membershipTransferFee" className="formInputLabel">
              Membership Transfer Fee <span className="required">*</span>
            </label>
            <input
              type="number"
              placeholder="Enter Membership Transfer Fee"
              className={`formInput ${
                formErrors.membershipTransferFee ? "formError" : ""
              }`}
              name="membershipTransferFee"
              value={formValues.membershipTransferFee}
              onChange={onChangeInput}
            />
            <span className="error-label">
              {formErrors.membershipTransferFee || ""}
            </span>
          </div>

          <div className="formInputBox span2">
            <label htmlFor="categoryDescription" className="formInputLabel">
              Category Description <span className="required">*</span>
            </label>

            {/* <textarea
              placeholder="Enter Category Description"
              className={`formInput ${
                formErrors.categoryDescription ? "formError" : ""
              }`}
              name="categoryDescription"
              value={formValues.categoryDescription}
              onChange={onChangeInput}
            /> */}

            <div className="commonWrapper textAreaCommonWrapper with-auto-height no-border">
              <ReactQuillTextEditor
                value={formValues.categoryDescription}
                setValue={(content) =>
                  onChangeTextInput(content, "categoryDescription")
                }
                isError={formErrors?.categoryDescription}
                loading={centralisedPlanCategoriesLoading}
              />
            </div>

            <span className="error-label">
              {formErrors.categoryDescription || ""}
            </span>
          </div>

          <div className="formInputBox checkbox-wrapper">
            <div className="flex-box">
              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formValues.isActive}
                  onChange={onChangeInput}
                />
                {formValues.isActive ? "Active" : "Inactive"}
              </label>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default PlanCategoryForm;
